import { forwardRef, type ButtonHTMLAttributes } from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex max-w-full items-center justify-center gap-1.5 whitespace-normal text-center break-words rounded-lg text-sm font-medium leading-snug ring-offset-background transition-all duration-200 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md transition-all duration-200",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:shadow-md",
        outline:
          "border border-marine-600 bg-white/95 text-marine-700 hover:bg-marine-50 hover:border-marine-700 hover:text-marine-800 shadow-sm hover:shadow-md backdrop-blur-sm",
        "hero-outline":
          "border border-white/50 bg-white/5 text-white hover:bg-white/10 hover:border-white/70 shadow-sm hover:shadow-md backdrop-blur-sm",
        secondary:
          "bg-gradient-to-br from-slate-50 to-slate-100 text-slate-700 hover:from-slate-100 hover:to-slate-200 shadow-sm hover:shadow-md border border-slate-200/50",
        ghost: "hover:bg-accent hover:text-accent-foreground transition-colors duration-200",
        link: "text-primary underline-offset-4 hover:underline",
        primary: "bg-gradient-to-br from-marine-400 to-marine-500 text-white hover:from-marine-500 hover:to-marine-600 shadow-sm hover:shadow-md border-0",
        success: "bg-gradient-to-br from-emerald-400 to-emerald-500 text-white hover:from-emerald-500 hover:to-emerald-600 shadow-sm hover:shadow-md border-0",
        warning: "bg-gradient-to-br from-orange-400 to-orange-500 text-white hover:from-orange-500 hover:to-orange-600 shadow-sm hover:shadow-md border-0",
        danger: "bg-gradient-to-br from-terracotta-500 to-terracotta-600 text-white hover:from-terracotta-600 hover:to-terracotta-700 shadow-sm hover:shadow-md border-0",
      },
      size: {
        default: "min-h-10 px-4 py-2 sm:min-h-9 sm:px-3.5",
        sm: "min-h-9 rounded-md px-3 py-1.5 sm:min-h-8 sm:px-2.5",
        lg: "min-h-12 rounded-md px-6 py-2.5 sm:min-h-11",
        icon: "h-10 w-10 shrink-0 p-0 sm:h-9 sm:w-9",
      },

    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
